/**
 * Отбор кандидатов в ядро.
 *
 * Общая часть двух путей — «придумать вопросы» и «взять готовый пакет»: оба
 * возвращают кандидатов, из которых человек отбирает нужные, и только явное
 * подтверждение через `POST /questions/bulk` меняет ядро. Так ревизия заводится
 * один раз на пачку, а не на каждый вопрос, и «до» и «после» остаются
 * сравнимыми.
 *
 * Квота проверяется до отправки: контракт отвергает превысившую пачку **целиком**
 * — частично принятое ядро нельзя ни объяснить клиенту, ни откатить. Поэтому
 * счётчик отобранного стоит рядом с кнопкой, а не всплывает ошибкой после.
 */

import { useState } from 'react';

import { api } from '../../api/client';
import { ErrorNotice } from '../../ui/state';
import { funnelStage } from '../../ui/labels';
import { countOf, int, percent } from '@shared/src/format';
import type { components } from '../../api/schema';

type Candidate = components['schemas']['QuestionCandidate'];
type BulkResult = components['schemas']['QuestionBulkResult'];

export function Candidates({
  brandId,
  candidates,
  free,
  reason,
  packId,
  note,
  onDone,
  onCancel,
}: {
  brandId: string;
  candidates: Candidate[];
  /** Сколько вопросов ещё помещается в тариф. `null` — квота неизвестна. */
  free: number | null;
  /** Что записать в причину новой ревизии ядра. */
  reason: string;
  packId?: string;
  note?: string;
  onDone: (result: BulkResult) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(candidates.map((candidate) => candidate.text)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const overQuota = free !== null && picked.size > free;

  function toggle(set: Set<string>, apply: (next: Set<string>) => void, key: string) {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    apply(next);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<BulkResult>(`/brands/${brandId}/questions/bulk`, {
        questions: candidates
          .filter((candidate) => picked.has(candidate.text))
          .map((candidate) => ({
            text: candidate.text,
            ...(candidate.cluster_key ? { cluster_key: candidate.cluster_key } : {}),
            ...(candidate.cluster_label
              ? { cluster_label: candidate.cluster_label }
              : {}),
            funnel_stage: candidate.funnel_stage,
          })),
        ...(packId ? { pack_id: packId } : {}),
        reason,
      });
      onDone(result);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {note ? <p className="stamp">{note}</p> : null}
      {error ? <ErrorNotice error={error} /> : null}

      <div className="tbl-wrap">
        <table className="mini">
          <caption>
            Кандидаты в ядро · отобрано {int(picked.size)} из {int(candidates.length)}
            {free === null
              ? ''
              : ` · свободно в тарифе ${countOf(free, 'вопрос', 'вопроса', 'вопросов')}`}
          </caption>
          <thead>
            <tr>
              <th scope="col">Брать</th>
              <th scope="col">Вопрос покупателя</th>
              <th scope="col">Тема</th>
              <th scope="col">Стадия</th>
              <th scope="col" className="tnum-col">
                Оценка
              </th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((candidate) => (
              <tr
                key={candidate.text}
                className={picked.has(candidate.text) ? 'picked' : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={picked.has(candidate.text)}
                    aria-label={`Взять вопрос: ${candidate.text}`}
                    onChange={() => toggle(picked, setPicked, candidate.text)}
                  />
                </td>
                <td>
                  <span className="q-text">{candidate.text}</span>
                  {candidate.rationale ? (
                    <span className="stamp">{candidate.rationale}</span>
                  ) : null}
                </td>
                <td>
                  {candidate.cluster_label ?? <span className="dim">без темы</span>}
                </td>
                <td>{funnelStage(candidate.funnel_stage)}</td>
                <td className="tnum tnum-col">{percent(candidate.score * 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overQuota ? (
        <p className="stamp under-table warn">
          Отобрано {int(picked.size)}, а в тариф помещается {int(free ?? 0)}. Пачку сверх
          квоты сервер отвергнет целиком — частично принятое ядро нельзя ни объяснить, ни
          откатить. Снимите лишние отметки.
        </p>
      ) : null}

      <div className="row-actions">
        <button
          className="btn btn-primary"
          type="button"
          disabled={busy || picked.size === 0 || overQuota}
          onClick={() => void submit()}
        >
          Добавить в ядро
        </button>
        <button
          className="btn btn-ghost"
          type="button"
          onClick={onCancel}
          disabled={busy}
        >
          Отмена
        </button>
        <button
          type="button"
          className="linkish"
          disabled={busy}
          onClick={() =>
            setPicked(new Set(picked.size === 0 ? candidates.map((c) => c.text) : []))
          }
        >
          {picked.size === 0 ? 'Отметить все' : 'Снять все отметки'}
        </button>
      </div>
    </>
  );
}
